/**
 * EXPORT CSV — UN SEUL ÉCRIVAIN, PARTAGÉ SERVEUR ET NAVIGATEUR (24/10/2026).
 *
 * ═══ POURQUOI CE FICHIER VIT ICI ═══
 *
 * Trois exports arrivent ensemble : la soirée et la nuit de comptabilité,
 * fabriquées par le SERVEUR, et l'historique des portes, assemblé dans le
 * NAVIGATEUR. Trois écritures des mêmes règles (séparateur, guillemets,
 * formules neutralisées, montants) finiraient par diverger, et la première
 * qui oublierait de neutraliser un « = » en tête ouvrirait une injection de
 * formule dans l'Excel du gérant. Ce module est donc volontairement SANS
 * AUCUNE dépendance : `packages/core` et `apps/api` l'importent par
 * `@gestion/shared/csv`, `apps/web` aussi, jamais par le barrel
 * `@gestion/shared` (il emporte pino).
 *
 * ═══ CE QUE PRODUIT CE MODULE ═══
 *
 *   · un BOM UTF-8 en tête : sans lui, Excel lit « Soirée » en « SoirÃ©e » ;
 *   · « ; » entre les colonnes, CRLF entre les lignes : l'Excel français
 *     ouvre le fichier d'un double clic, sans assistant d'import ;
 *   · chaque cellule TOUJOURS entre guillemets, même vide (`""`), les
 *     guillemets internes doublés ;
 *   · un texte qui commence par = + - @, une tabulation ou un retour chariot
 *     reçoit le préfixe ' (recommandation OWASP « CSV injection ») ;
 *   · un montant en centimes ENTIERS rendu « 1234,50 », par calcul entier :
 *     ni Intl (l'espace insécable des milliers casserait le nombre dans
 *     Excel), ni flottant, ni préfixe. Un montant absent donne une cellule
 *     VIDE, jamais « 0,00 » : un zéro inventé se lirait comme un chiffre.
 *
 * Aucune date ici : chaque appelant met en forme les siennes (luxon côté
 * cœur, Intl côté navigateur), dans le fuseau de Paris.
 */
/** Marque d'ordre des octets : Excel en a besoin pour lire l'UTF-8. */
export const CSV_BOM = '\uFEFF';
/** Séparateur de colonnes : le point-virgule de l'Excel français. */
export const CSV_SEPARATEUR = ';';
/** Fin de ligne : CRLF (RFC 4180). */
export const CSV_FIN_DE_LIGNE = '\r\n';
/** La cellule vide : `""`. */
export const CELLULE_VIDE = '""';
/** Les caractères qui, en tête d'une cellule, feraient d'un texte une formule. */
const TETE_DE_FORMULE = /^[=+\-@\t\r]/;
function entreGuillemets(s) {
    return `"${s.replace(/"/g, '""')}"`;
}
/**
 * Un texte. `null` ou `undefined` donnent une cellule vide ; un retour à la
 * ligne (CR, LF ou CRLF) devient une espace, pour qu'une ligne du tableur
 * reste une ligne du fichier ; une tête de formule reçoit le préfixe '.
 * Le préfixe se décide sur le texte REÇU : « \rx » est neutralisé avant que
 * son CR ne devienne une espace.
 */
export function celluleTexte(v) {
    if (v === null || v === undefined)
        return CELLULE_VIDE;
    const s = String(v);
    const neutralise = TETE_DE_FORMULE.test(s) ? `'${s}` : s;
    return entreGuillemets(neutralise.replace(/\r\n|\r|\n/g, ' '));
}
/**
 * Un montant en centimes ENTIERS : « 1234,50 », « -12,50 », « 0,05 »,
 * « 0,00 ». Calcul entier, sans séparateur de milliers ni préfixe : le
 * signe moins d'un montant n'est pas une formule. `null`, `undefined`, NaN,
 * un non-entier ou un entier hors de la plage sûre donnent une cellule VIDE.
 */
export function celluleMontant(centimes) {
    if (typeof centimes !== 'number' || !Number.isSafeInteger(centimes))
        return CELLULE_VIDE;
    const negatif = centimes < 0;
    const abs = Math.abs(centimes);
    const reste = abs % 100;
    const euros = (abs - reste) / 100;
    return entreGuillemets(`${negatif ? '-' : ''}${euros},${String(reste).padStart(2, '0')}`);
}
/**
 * Un nombre entier (billets, scannés, lignes) : « 312 », « -3 ». Même
 * raison que le montant : passé par `celluleTexte`, un entier négatif
 * recevrait le préfixe ' et ne serait plus un nombre pour Excel. Un
 * non-entier ou une valeur absente donnent une cellule vide.
 */
export function celluleNombre(n) {
    if (typeof n !== 'number' || !Number.isSafeInteger(n))
        return CELLULE_VIDE;
    return entreGuillemets(String(n === 0 ? 0 : n));
}
/**
 * Un pourcentage DÉJÀ multiplié par cent, à une décimale : 87.53 donne
 * « 87,5 », 100 donne « 100,0 ». Une valeur absente ou non finie donne une
 * cellule vide.
 */
export function cellulePourcent(x) {
    if (typeof x !== 'number' || !Number.isFinite(x))
        return CELLULE_VIDE;
    const dixiemes = Math.round(Math.abs(x) * 10);
    if (!Number.isSafeInteger(dixiemes))
        return CELLULE_VIDE;
    const negatif = x < 0 && dixiemes !== 0;
    const reste = dixiemes % 10;
    return entreGuillemets(`${negatif ? '-' : ''}${(dixiemes - reste) / 10},${reste}`);
}
/**
 * Le document : le BOM, l'en-tête (des libellés, mis en cellules ici), puis
 * les lignes, jointes par CRLF, sans fin de ligne après la dernière. Une
 * ligne qui n'a pas autant de colonnes que l'en-tête lève une erreur : un
 * décalage d'une colonne mettrait un montant sous le mauvais titre.
 */
export function documentCsv(entete, lignes) {
    if (entete.length === 0)
        throw new Error('CSV : l’en-tête ne peut pas être vide.');
    const n = entete.length;
    const corps = [entete.map((t) => celluleTexte(t)).join(CSV_SEPARATEUR)];
    lignes.forEach((ligne, i) => {
        if (ligne.length !== n) {
            throw new Error(`CSV : la ligne ${i + 1} a ${ligne.length} colonnes, l’en-tête en a ${n}.`);
        }
        corps.push(ligne.join(CSV_SEPARATEUR));
    });
    return CSV_BOM + corps.join(CSV_FIN_DE_LIGNE);
}
/** Les ligatures que la décomposition NFD ne sépare pas. */
const LIGATURES = { œ: 'oe', Œ: 'oe', æ: 'ae', Æ: 'ae', ß: 'ss' };
/** Une partie de nom de fichier : ASCII, minuscules, tirets, 50 caractères au plus. */
function slugPartie(partie) {
    const s = String(partie)
        .replace(/[œŒæÆß]/g, (c) => LIGATURES[c] ?? '-')
        .normalize('NFD')
        .replace(/\p{M}/gu, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 50)
        .replace(/-+$/, '');
    return s || 'sans-nom';
}
/**
 * Le nom du fichier : chaque partie en slug ASCII (accents retirés,
 * minuscules, tout le reste en tirets, 50 caractères au plus, « sans-nom »
 * pour une partie vide), jointes par un tiret, puis « .csv ».
 * `nomFichierCsv('soiree', '2026-10-24', 'L’Étage')` donne
 * « soiree-2026-10-24-l-etage.csv ». Jamais de guillemet, de barre oblique,
 * d'antislash ni de retour à la ligne : le nom va dans un en-tête HTTP.
 */
export function nomFichierCsv(...parties) {
    const morceaux = parties.length > 0 ? parties.map(slugPartie) : ['sans-nom'];
    return `${morceaux.join('-')}.csv`;
}
/** RFC 5987 : encodeURIComponent laisse passer ' ( ) * qui n'ont pas leur place dans filename*. */
function encodeRfc5987(s) {
    return encodeURIComponent(s).replace(/['()*]/g, (c) => `%${c.charCodeAt(0).toString(16).toUpperCase()}`);
}
/**
 * La valeur de l'en-tête Content-Disposition :
 * `attachment; filename="…"; filename*=UTF-8''…`. Le nom attendu sort de
 * `nomFichierCsv` ; un autre nom est tout de même rendu sûr : la forme
 * entre guillemets ne garde que l'ASCII imprimable, sans " ni \ (le reste
 * devient « _ »), la forme étendue encode tout.
 */
export function dispositionPieceJointe(nom) {
    const ascii = String(nom).replace(/[^\x20-\x7E]|["\\]/g, '_');
    return `attachment; filename="${ascii}"; filename*=UTF-8''${encodeRfc5987(String(nom))}`;
}
//# sourceMappingURL=csv.js.map